export interface FloorplanAutoStats {
  avgTurnDays: number | null;
  avgVehicleValue: number | null;
  annualFloorplanCost: number | null;
  portfolioUnits: number;
  totalDailyCharge: number | null;
  avgCostPerUnit: number | null;
  avgDaysPerUnit: number | null;
  avgDailyRatePerUnit: number | null;
  method: "receivables" | "statement" | null;
}

const EMPTY: FloorplanAutoStats = {
  avgTurnDays: null, avgVehicleValue: null, annualFloorplanCost: null,
  portfolioUnits: 0, totalDailyCharge: null, avgCostPerUnit: null,
  avgDaysPerUnit: null, avgDailyRatePerUnit: null, method: null,
};

export function computeFloorplanStats(statements: any[]): FloorplanAutoStats {
  if (!statements.length) return EMPTY;
  const seenVins = new Set<string>();
  const allFlooredUnits: Array<{ cost: number; days: number; principal: number }> = [];
  const chargeUnits: Array<{ cost: number; days: number; principal: number }> = [];

  for (const s of statements) {
    try {
      for (const r of JSON.parse(s.receivablesJson || "[]")) {
        const principal = r.principal ?? 0;
        if (principal <= 0) continue;
        const vin = typeof r.vin === "string" && r.vin.trim().length >= 5
          ? r.vin.trim().toUpperCase() : null;
        if (vin && seenVins.has(vin)) continue;
        if (vin) seenVins.add(vin);
        const cost = (r.interest ?? 0) + (r.fees ?? 0);
        const days = r.daysOutstanding ?? r.daysFloored ?? 0;
        allFlooredUnits.push({ cost, days, principal });
        if (days > 0 && cost > 0) chargeUnits.push({ cost, days, principal });
      }
    } catch {}
  }

  if (allFlooredUnits.length > 0) {
    const portfolioUnits = allFlooredUnits.length;
    const avgPrincipal = allFlooredUnits.reduce((s, u) => s + u.principal, 0) / portfolioUnits;
    const src = chargeUnits.length > 0 ? chargeUnits : allFlooredUnits;
    const totalDailyCharge = chargeUnits.length > 0
      ? chargeUnits.reduce((s, u) => s + u.cost / u.days, 0) : null;
    const avgDays = src.reduce((s, u) => s + u.days, 0) / src.length;
    const avgCost = src.reduce((s, u) => s + u.cost, 0) / src.length;
    const avgDailyRate = avgDays > 0 && avgCost > 0 ? avgCost / avgDays : null;
    return {
      avgTurnDays: Math.round(avgDays), avgVehicleValue: Math.round(avgPrincipal),
      annualFloorplanCost: totalDailyCharge != null ? Math.round(totalDailyCharge * 365) : null,
      portfolioUnits, totalDailyCharge,
      avgCostPerUnit: avgCost > 0 ? Math.round(avgCost * 100) / 100 : null,
      avgDaysPerUnit: Math.round(avgDays * 10) / 10,
      avgDailyRatePerUnit: avgDailyRate != null ? Math.round(avgDailyRate * 100) / 100 : null,
      method: "receivables",
    };
  }

  let total = 0;
  const avgDaysArr: number[] = [];
  for (const s of statements) {
    const interest = parseFloat(s.totalInterestPaid || "0");
    const fees = parseFloat(s.totalFeesPaid || "0");
    total += ((interest + fees) / (s.periodDays || 30)) * 365;
    const days = parseFloat(s.avgDaysInStock || "0");
    if (days > 0) avgDaysArr.push(days);
  }
  return {
    ...EMPTY,
    avgTurnDays: avgDaysArr.length ? Math.round(avgDaysArr.reduce((a, b) => a + b, 0) / avgDaysArr.length) : null,
    annualFloorplanCost: total > 0 ? Math.round(total) : null,
    method: total > 0 ? "statement" : null,
  };
}